/**
 * URL Builder Utility for Frontend Service
 *
 * Centralizes URL construction for frontend API calls and health checks.
 * Handles browser-side (VITE_API_URL) and server-side (API_SERVICE_URL) URLs.
 */

import logger from './logger';

type Environment = 'local' | 'docker' | 'railway';

/**
 * Detect current environment (browser context)
 */
function detectEnvironment(): Environment {
  // Check for Railway environment
  if (import.meta.env.VITE_RAILWAY_ENVIRONMENT || import.meta.env.VITE_RAILWAY_PROJECT_ID) {
    return 'railway';
  }

  // Check if API URL suggests Docker
  const apiUrl = import.meta.env.VITE_API_URL || '';
  if (apiUrl.includes('api:3003')) {
    return 'docker';
  }

  return 'local';
}

/**
 * Detect server-side environment (health server context)
 */
function detectServerEnvironment(): Environment {
  if (process.env.RAILWAY_ENVIRONMENT || process.env.RAILWAY_PROJECT_ID) {
    return 'railway';
  }

  const apiUrl = process.env.API_SERVICE_URL || process.env.VITE_API_URL || '';
  if (apiUrl.includes('api:3003')) {
    return 'docker';
  }

  return 'local';
}

/**
 * URL Builder for Frontend (Browser Context)
 */
export class FrontendUrlBuilder {
  private readonly environment: Environment;
  private readonly apiUrl: string;

  constructor() {
    this.environment = detectEnvironment();
    this.apiUrl = this.getApiUrl();
  }

  /**
   * Get environment type
   */
  getEnvironment(): Environment {
    return this.environment;
  }

  /**
   * Get API base URL for browser requests
   */
  private getApiUrl(): string {
    if (import.meta.env.VITE_API_URL) {
      return import.meta.env.VITE_API_URL;
    }

      // Try to read storage port from environment
    const apiPort = import.meta.env.VITE_API_SERVICE_PORT || '3003';

    // Fallback based on environment
    switch (this.environment) {
      case 'railway':
        throw new Error('VITE_API_URL must be set in Railway environment');
      case 'docker':
        // In Docker, frontend container makes requests to host's localhost
        // (not to api:3003 which is internal Docker network)
        return `http://localhost:${apiPort}`;
      case 'local':
      default:
        return `http://localhost:${apiPort}`;
    }
  }

  /**
   * Get API base URL
   */
  getApiBaseUrl(): string {
    return this.apiUrl;
  }

  /**
   * Build API endpoint URL
   */
  buildApiUrl(path: string): string {
    // Ensure path starts with /
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${this.apiUrl}${cleanPath}`;
  }

  /**
   * Build API endpoint with /api prefix
   */
  buildApiEndpoint(path: string): string {
    // Remove leading / if present
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    return this.buildApiUrl(`/api/${cleanPath}`);
  }

  /**
   * Build storage health URL
   */
  buildStorageHealthUrl(): string {
    // Check for explicit environment variable first
    if (import.meta.env.VITE_STORAGE_HEALTH_URL) {
      return import.meta.env.VITE_STORAGE_HEALTH_URL;
    }

    // Try to read storage port from environment
    const storagePort = import.meta.env.VITE_STORAGE_SERVICE_PORT || '3001';

    // Fallback based on environment
    switch (this.environment) {
      case 'railway':
        // In Railway, would use internal service URL
        return import.meta.env.VITE_STORAGE_URL || `http://localhost:${storagePort}/health`;
      case 'docker':
      case 'local':
      default:
        return `http://localhost:${storagePort}/health`;
    }
  }

  /**
   * Build database health URL
   */
  buildDatabaseHealthUrl(): string {
    // Check for explicit environment variable first
    if (import.meta.env.VITE_DATABASE_HEALTH_URL) {
      return import.meta.env.VITE_DATABASE_HEALTH_URL;
    }

    // Try to read database port from environment
    const databasePort = import.meta.env.VITE_DATABASE_SERVICE_PORT || '3002';

    // Fallback based on environment
    switch (this.environment) {
      case 'railway':
        // In Railway, would use internal service URL
        return import.meta.env.VITE_DATABASE_URL || `http://localhost:${databasePort}/health`;
      case 'docker':
      case 'local':
      default:
        return `http://localhost:${databasePort}/health`;
    }
  }

  /**
   * Build frontend health URL
   */
  buildFrontendHealthUrl(): string {
    // Check for explicit environment variable first
    if (import.meta.env.VITE_FRONTEND_HEALTH_URL) {
      return import.meta.env.VITE_FRONTEND_HEALTH_URL;
    }

    // Try to read health port from environment
    const healthPort = import.meta.env.VITE_HEALTH_PORT || '3100';

    // Fallback based on environment
    switch (this.environment) {
      case 'railway':
        // In Railway, would use internal service URL
        return `http://localhost:${healthPort}/health`;
      case 'docker':
      case 'local':
      default:
        return `http://localhost:${healthPort}/health`;
    }
  }

  /**
   * Log current configuration (for debugging)
   */
  logConfiguration(): void {
    logger.info('UrlBuilder', 'FrontendUrlBuilder Configuration:', {
      environment: this.environment,
      apiUrl: this.apiUrl
    });
  }
}

/**
 * URL Builder for Frontend Health Server (Server Context)
 */
export class HealthServerUrlBuilder {
  private readonly environment: Environment;
  private readonly apiUrl: string;

  constructor() {
    this.environment = detectServerEnvironment();
    this.apiUrl = this.getApiUrl();
  }

  /**
   * Get environment type
   */
  getEnvironment(): Environment {
    return this.environment;
  }

  /**
   * Get API URL for server-side health checks
   * Uses API_SERVICE_URL for internal Docker/Railway communication
   */
  private getApiUrl(): string {
    // Priority: API_SERVICE_URL (Docker/Railway) > VITE_API_URL (local)
    if (process.env.API_SERVICE_URL) {
      return process.env.API_SERVICE_URL;
    }

    if (process.env.VITE_API_URL) {
      return process.env.VITE_API_URL;
    }

    // Fallback based on environment
    switch (this.environment) {
      case 'railway':
        throw new Error('API_SERVICE_URL must be set in Railway environment');
      case 'docker':
        return 'http://api:3003';
      case 'local':
      default:
        return 'http://localhost:3003';
    }
  }

  /**
   * Get API health check URL
   */
  getApiHealthUrl(): string {
    return `${this.apiUrl}/api/health`;
  }

  /**
   * Log current configuration (for debugging)
   */
  logConfiguration(): void {
    logger.info('UrlBuilder', 'HealthServerUrlBuilder Configuration:', {
      environment: this.environment,
      apiUrl: this.apiUrl
    });
  }
}

// Singleton instances
let frontendUrlBuilderInstance: FrontendUrlBuilder | null = null;
let healthServerUrlBuilderInstance: HealthServerUrlBuilder | null = null;

/**
 * Get singleton frontend URL builder instance (browser context)
 */
export function getFrontendUrlBuilder(): FrontendUrlBuilder {
  if (!frontendUrlBuilderInstance) {
    frontendUrlBuilderInstance = new FrontendUrlBuilder();
  }
  return frontendUrlBuilderInstance;
}

/**
 * Get singleton health server URL builder instance (server context)
 */
export function getHealthServerUrlBuilder(): HealthServerUrlBuilder {
  if (!healthServerUrlBuilderInstance) {
    healthServerUrlBuilderInstance = new HealthServerUrlBuilder();
  }
  return healthServerUrlBuilderInstance;
}

/**
 * Reset singletons (for testing)
 */
export function resetFrontendUrlBuilders(): void {
  frontendUrlBuilderInstance = null;
  healthServerUrlBuilderInstance = null;
}
